// 🔐 Permission Assignment - Asignación Masiva de Permisos por Perfil
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Shield,
  Users,
  CheckCircle2,
  Loader2,
  Save,
  AlertCircle,
  Menu,
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronsDown,
  ChevronsRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

interface Perfil {
  id: string;
  nombre: string;
  descripcion: string | null;
  nivel_acceso: number;
}

interface Empresa {
  id: string;
  nombre: string;
}

interface MenuOption {
  id: string;
  nombre: string;
  ruta: string;
  icon: string | null;
  submenu: string;
  menu: string;
}

interface PermissionAssignment {
  opcion_id: string;
  permissions: string[];
}

const AVAILABLE_PERMISSIONS = [
  { key: 'crear', label: 'Crear', description: 'Permite crear nuevos registros' },
  { key: 'editar', label: 'Editar', description: 'Permite editar registros existentes' },
  { key: 'eliminar', label: 'Eliminar', description: 'Permite eliminar registros' },
  { key: 'xls', label: 'Excel', description: 'Permite exportar a Excel' },
  { key: 'pdf', label: 'PDF', description: 'Permite exportar a PDF' },
  { key: 'imprimir', label: 'Imprimir', description: 'Permite imprimir documentos' },
  { key: 'aprobar', label: 'Aprobar', description: 'Permite aprobar documentos' },
  { key: 'anular', label: 'Anular', description: 'Permite anular documentos' },
];

export function PermissionAssignment() {
  const { t } = useTranslation('admin');
  const dlg = useDialogoEnLinea();
  const queryClient = useQueryClient();

  const [selectedEmpresa, setSelectedEmpresa] = useState<string>('');
  const [selectedPerfil, setSelectedPerfil] = useState<string>('');
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [globalPermissions, setGlobalPermissions] = useState<string[]>([]);

  // Estados para colapsar/expandir
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [expandedSubmenus, setExpandedSubmenus] = useState<Set<string>>(new Set());

  // Cargar empresas
  const { data: empresas, isLoading: isLoadingEmpresas } = useQuery({
    queryKey: ['empresas'],
    queryFn: async () => {
      const response = await apiClient.get('/rest/autenticacion.empresas?select=id,nombre&order=nombre');
      return response.data as Empresa[];
    },
  });

  // Cargar perfiles
  const { data: perfiles, isLoading: isLoadingPerfiles } = useQuery({
    queryKey: ['perfiles'],
    queryFn: async () => {
      const response = await apiClient.get('/rest/autenticacion.perfiles?select=id,nombre,nivel_acceso&order=nivel_acceso.desc');
      return response.data as Perfil[];
    },
  });

  // Cargar todas las opciones de menú
  const { data: menuOptions, isLoading: isLoadingOptions } = useQuery({
    queryKey: ['allMenuOptions'],
    queryFn: async () => {
      const response = await apiClient.post('/rpc/get_all_menu_options', {});
      return response.data as MenuOption[];
    },
  });

  // Cargar permisos existentes para el perfil seleccionado
  const { data: existingPermissions, refetch: refetchPermissions, isLoading: isLoadingPermissions } = useQuery({
    queryKey: ['permissions', selectedEmpresa, selectedPerfil],
    queryFn: async () => {
      if (!selectedEmpresa || !selectedPerfil) return null;

      const response = await apiClient.post('/rpc/get_permissions_by_perfil', {
        _empresa_id: selectedEmpresa,
        _perfil_id: selectedPerfil,
      });

      // Devuelve un array: [{ opcionId, permissions }, ...]
      // Convertir a objeto: { opcionId: permissions }
      const permissionsData = response.data;

      if (Array.isArray(permissionsData)) {
        const permissionsObject: Record<string, string[]> = {};
        permissionsData.forEach((item: { opcionId: string; permissions: string[] }) => {
          permissionsObject[item.opcionId] = item.permissions;
        });
        return permissionsObject;
      }

      return permissionsData as Record<string, string[]>;
    },
    enabled: !!selectedEmpresa && !!selectedPerfil,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Actualizar permisos locales cuando se cargan los existentes
  useEffect(() => {
    if (existingPermissions !== null && existingPermissions !== undefined) {
      setPermissions(existingPermissions);
    }
  }, [existingPermissions]);

  // Mutation para guardar permisos
  const saveMutation = useMutation({
    mutationFn: async () => {
      // Una opción sin ningún permiso marcado está DESMARCADA: no se envía
      // (si se enviara, quedaría asignada/visible en el menú con permisos vacíos)
      const assignments: PermissionAssignment[] = Object.entries(permissions)
        .filter(([, perms]) => (perms?.length ?? 0) > 0)
        .map(([opcion_id, perms]) => ({
          opcion_id,
          permissions: perms,
        }));

      const response = await apiClient.post('/rpc/assign_permissions', {
        _empresa_id: selectedEmpresa,
        _perfil_id: selectedPerfil,
        _assignments: assignments,
      });

      return response.data;
    },
    onSuccess: () => {
      toast.success(t('pa_ok_guardado'));
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      refetchPermissions();
    },
    onError: (error: any) => {
      toast.error(
        t('pa_err_guardar') +
        (error.response?.data?.message || error.message)
      );
    },
  });

  // Nivel del perfil seleccionado (para bloquear el menú Sistemas a nivel < 5)
  const perfilNivel = perfiles?.find((p) => p.id === selectedPerfil)?.nivel_acceso ?? 0;
  const sistemasBloqueado = (menu: string) => menu === 'Sistemas' && perfilNivel < 5;

  // Toggle permiso individual
  const togglePermission = (opcionId: string, permission: string) => {
    setPermissions((prev) => {
      const current = prev[opcionId] || [];
      const newPermissions = current.includes(permission)
        ? current.filter((p) => p !== permission)
        : [...current, permission];

      return {
        ...prev,
        [opcionId]: newPermissions,
      };
    });
  };

  // Aplicar permisos globales a todas las opciones
  const applyGlobalPermissions = () => {
    if (globalPermissions.length === 0) {
      toast.error(t('pa_err_global'));
      return;
    }

    const newPermissions: Record<string, string[]> = {};
    menuOptions?.forEach((option) => {
      newPermissions[option.id] = [...globalPermissions];
    });

    setPermissions(newPermissions);
    toast.success(t('pa_aplicados', { n: menuOptions?.length || 0 }));
  };

  // Limpiar todos los permisos
  const clearAllPermissions = async () => {
    if (await dlg.confirmar(t('pa_confirm_limpiar'), { peligro: true })) {
      setPermissions({});
      toast.success(t('pa_limpiados'));
    }
  };

  // ---- Marcado MASIVO por menú / submenú ----
  // Marca = aplica los permisos globales seleccionados (o TODOS si no hay selección);
  // desmarca = deja las opciones del grupo sin permisos.
  const aplicarAGrupo = (options: MenuOption[], marcar: boolean) => {
    const set = marcar
      ? (globalPermissions.length ? [...globalPermissions] : AVAILABLE_PERMISSIONS.map((x) => x.key))
      : [];
    setPermissions((prev) => {
      const next = { ...prev };
      options.forEach((o) => { if (marcar) next[o.id] = [...set]; else delete next[o.id]; });
      return next;
    });
    toast.success(marcar
      ? t('pa_marcadas', { n: options.length, p: set.length })
      : t('pa_desmarcadas', { n: options.length }));
  };
  const estadoGrupo = (options: MenuOption[]): 'todos' | 'algunos' | 'ninguno' => {
    const con = options.filter((o) => (permissions[o.id] || []).length > 0).length;
    return con === 0 ? 'ninguno' : con === options.length ? 'todos' : 'algunos';
  };

  // Toggle expandir/colapsar menú
  const toggleMenu = (menuName: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuName)) {
        newSet.delete(menuName);
      } else {
        newSet.add(menuName);
      }
      return newSet;
    });
  };

  // Toggle expandir/colapsar submenú
  const toggleSubmenu = (submenuKey: string) => {
    setExpandedSubmenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(submenuKey)) {
        newSet.delete(submenuKey);
      } else {
        newSet.add(submenuKey);
      }
      return newSet;
    });
  };

  // Expandir/colapsar todos los menús
  const toggleAllMenus = () => {
    if (expandedMenus.size === 0) {
      // Expandir todos
      setExpandedMenus(new Set(Object.keys(groupedOptions || {})));
      // También expandir todos los submenús
      const allSubmenuKeys: string[] = [];
      Object.entries(groupedOptions || {}).forEach(([menu, submenus]) => {
        Object.keys(submenus).forEach((submenu) => {
          allSubmenuKeys.push(`${menu}_${submenu}`);
        });
      });
      setExpandedSubmenus(new Set(allSubmenuKeys));
    } else {
      // Colapsar todos
      setExpandedMenus(new Set());
      setExpandedSubmenus(new Set());
    }
  };

  // Agrupar opciones por menú y submenú
  const groupedOptions = menuOptions?.reduce((acc, option) => {
    const menuKey = option.menu;
    const submenuKey = option.submenu;

    if (!acc[menuKey]) {
      acc[menuKey] = {};
    }
    if (!acc[menuKey][submenuKey]) {
      acc[menuKey][submenuKey] = [];
    }

    acc[menuKey][submenuKey].push(option);
    return acc;
  }, {} as Record<string, Record<string, MenuOption[]>>);

  const handleSave = () => {
    if (!selectedEmpresa || !selectedPerfil) {
      toast.error(t('pa_err_sel'));
      return;
    }

    saveMutation.mutate();
  };

  const canSave = selectedEmpresa && selectedPerfil && Object.keys(permissions).length > 0;

  return (
    <div className="space-y-6 p-6">
      {dlg.ui}
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t('pa_titulo')}
            </h1>
            <p className="text-muted-foreground">
              {t('pa_sub')}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Selector de Empresa y Perfil */}
      <Card className="p-6">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {/* Empresa */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <label className="text-sm font-medium">{t('pa_empresa')}</label>
              </div>
              {isLoadingEmpresas ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">{t('pa_cargando')}</span>
                </div>
              ) : (
                <select
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  value={selectedEmpresa}
                  onChange={(e) => {
                    setSelectedEmpresa(e.target.value);
                    setSelectedPerfil(''); // Limpiar perfil al cambiar empresa
                    setPermissions({});
                  }}
                >
                  <option value="">{t('pa_sel_empresa')}</option>
                  {empresas?.map((empresa) => (
                    <option key={empresa.id} value={empresa.id}>
                      {empresa.nombre}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Perfil */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <label className="text-sm font-medium">{t('pa_perfil')}</label>
              </div>
              {isLoadingPerfiles ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">{t('pa_cargando')}</span>
                </div>
              ) : (
                <select
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  value={selectedPerfil}
                  onChange={(e) => {
                    setSelectedPerfil(e.target.value);
                    setPermissions({}); // Limpiar permisos al cambiar perfil (se recargarán automáticamente)
                  }}
                  disabled={!selectedEmpresa}
                >
                  <option value="">{t('pa_sel_perfil')}</option>
                  {perfiles?.map((perfil) => (
                    <option key={perfil.id} value={perfil.id}>
                      {t('pa_perfil_nivel', { nombre: perfil.nombre, nivel: perfil.nivel_acceso })}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {selectedEmpresa && selectedPerfil && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-lg bg-green-50 dark:bg-green-900/20 p-3 flex items-center gap-2"
            >
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm text-green-700 dark:text-green-300">
                {t('pa_listo')}
              </span>
            </motion.div>
          )}
        </div>
      </Card>

      {/* Permisos Globales */}
      {selectedEmpresa && selectedPerfil && (
        <Card className="p-6">
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {t('pa_globales')}
            </h3>

            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_PERMISSIONS.map((perm) => (
                  <label
                    key={perm.key}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-input hover:bg-muted cursor-pointer"
                    title={t(`pa_perm_${perm.key}_desc`)}
                  >
                    <input
                      type="checkbox"
                      checked={globalPermissions.includes(perm.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setGlobalPermissions([...globalPermissions, perm.key]);
                        } else {
                          setGlobalPermissions(globalPermissions.filter((p) => p !== perm.key));
                        }
                      }}
                      className="h-4 w-4 rounded border-input cursor-pointer"
                    />
                    <span className="text-sm font-medium">{t(`pa_perm_${perm.key}`)}</span>
                  </label>
                ))}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={applyGlobalPermissions}
                  variant="outline"
                  size="sm"
                  disabled={globalPermissions.length === 0}
                >
                  {t('pa_aplicar_todas')}
                </Button>
                <Button
                  onClick={clearAllPermissions}
                  variant="outline"
                  size="sm"
                >
                  {t('pa_limpiar_todo')}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Lista de Opciones con Permisos */}
      {selectedEmpresa && selectedPerfil && (
        <Card className="p-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Menu className="h-5 w-5 text-primary" />
                {t('pa_por_opcion')}
              </h3>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  {t('pa_configuradas', { n: Object.keys(permissions).length, total: menuOptions?.length || 0 })}
                </span>
                <Button
                  onClick={toggleAllMenus}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  {expandedMenus.size === 0 ? (
                    <>
                      <ChevronsDown className="h-4 w-4" />
                      {t('pa_expandir')}
                    </>
                  ) : (
                    <>
                      <ChevronsRight className="h-4 w-4" />
                      {t('pa_colapsar')}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {isLoadingOptions || isLoadingPermissions ? (
              <div className="flex items-center justify-center p-12">
                <div className="space-y-4 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    {isLoadingOptions ? t('pa_cargando_opc') : t('pa_cargando_perm')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {groupedOptions &&
                  Object.entries(groupedOptions).map(([menu, submenus]) => {
                    const isMenuExpanded = expandedMenus.has(menu);
                    const submenuCount = Object.keys(submenus).length;
                    const optionCount = Object.values(submenus).flat().length;

                    return (
                      <div key={menu} className="border border-border rounded-lg overflow-hidden">
                        {/* Header del Menú (expandir + marcado masivo) */}
                        <div className="w-full flex items-center justify-between p-4 bg-muted/50 hover:bg-muted transition-colors">
                          <button onClick={() => toggleMenu(menu)} className="flex flex-1 items-center gap-2 text-left">
                            {isMenuExpanded ? (
                              <ChevronDown className="h-5 w-5 text-primary" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-primary" />
                            )}
                            <h4 className="font-semibold text-lg">{menu}</h4>
                            <span className="text-xs text-muted-foreground">
                              {t('pa_menu_count', { s: submenuCount, o: optionCount })}
                            </span>
                          </button>
                          {!sistemasBloqueado(menu) && (() => {
                            const todas = Object.values(submenus).flat();
                            const est = estadoGrupo(todas);
                            return (
                              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap"
                                title={t('pa_todo_menu_tip')}>
                                <input type="checkbox" checked={est === 'todos'}
                                  ref={(el) => { if (el) el.indeterminate = est === 'algunos'; }}
                                  onChange={() => aplicarAGrupo(todas, est !== 'todos')} />
                                {t('pa_todo_menu')}
                              </label>
                            );
                          })()}
                        </div>

                        {/* Contenido del Menú */}
                        {isMenuExpanded && (
                          <div className="p-4 space-y-3">
                            {Object.entries(submenus).map(([submenu, options]) => {
                              const submenuKey = `${menu}_${submenu}`;
                              const isSubmenuExpanded = expandedSubmenus.has(submenuKey);

                              return (
                                <div key={submenuKey} className="border border-border rounded-lg overflow-hidden">
                                  {/* Header del Submenú (expandir + marcado masivo) */}
                                  <div className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors">
                                    <button onClick={() => toggleSubmenu(submenuKey)} className="flex flex-1 items-center gap-2 text-left">
                                      {isSubmenuExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                      <h5 className="font-medium text-sm">{submenu}</h5>
                                      <span className="text-xs text-muted-foreground">
                                        {t('pa_sub_count', { o: options.length })}
                                      </span>
                                    </button>
                                    {!sistemasBloqueado(menu) && (() => {
                                      const est = estadoGrupo(options);
                                      return (
                                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap"
                                          title={t('pa_todo_sub_tip')}>
                                          <input type="checkbox" checked={est === 'todos'}
                                            ref={(el) => { if (el) el.indeterminate = est === 'algunos'; }}
                                            onChange={() => aplicarAGrupo(options, est !== 'todos')} />
                                          {t('pa_todo')}
                                        </label>
                                      );
                                    })()}
                                  </div>

                                  {/* Opciones del Submenú */}
                                  {isSubmenuExpanded && (
                                    <div className="p-3 space-y-2 bg-background">
                                      {options.map((option) => {
                                        const hasPermissions = (permissions[option.id] || []).length > 0;
                                        const optionPermissions = permissions[option.id] || [];
                                        const bloqueado = sistemasBloqueado(option.menu);

                                        return (
                                          <div
                                            key={option.id}
                                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                                              bloqueado
                                                ? 'border-border bg-muted/40 opacity-60'
                                                : hasPermissions
                                                ? 'border-primary/50 bg-primary/5'
                                                : 'border-border hover:bg-muted/50'
                                            }`}
                                          >
                                            <div className="flex-1 min-w-0 mr-4">
                                              <p className="font-medium text-sm truncate">{option.nombre}</p>
                                              <p className="text-xs text-muted-foreground truncate">
                                                {bloqueado ? t('pa_solo_admin') : option.ruta}
                                              </p>
                                            </div>

                                            <div className="flex gap-1 flex-wrap justify-end">
                                              {AVAILABLE_PERMISSIONS.map((perm) => {
                                                const isChecked = optionPermissions.includes(perm.key);

                                                return (
                                                  <label
                                                    key={perm.key}
                                                    className={`flex items-center gap-1 px-2 py-1 rounded border text-xs transition-colors ${
                                                      bloqueado
                                                        ? 'border-input opacity-50 cursor-not-allowed'
                                                        : isChecked
                                                        ? 'border-primary bg-primary text-primary-foreground cursor-pointer'
                                                        : 'border-input hover:bg-muted cursor-pointer'
                                                    }`}
                                                    title={bloqueado ? t('pa_sistemas_tip') : t(`pa_perm_${perm.key}_desc`)}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      checked={isChecked}
                                                      disabled={bloqueado}
                                                      onChange={() => { if (!bloqueado) togglePermission(option.id, perm.key); }}
                                                      className="h-3 w-3 rounded border-input cursor-pointer disabled:cursor-not-allowed"
                                                    />
                                                    <span>{t(`pa_perm_${perm.key}`)}</span>
                                                  </label>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Botón Guardar */}
      {selectedEmpresa && selectedPerfil && (
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span>
                {t('pa_pie')}
              </span>
            </div>

            <Button
              onClick={handleSave}
              disabled={!canSave || saveMutation.isPending}
              size="lg"
              className="gap-2"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('pa_guardando')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t('pa_guardar')}
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
